import React, { useState, useEffect } from 'react'
import { useAppSelector } from '@store/hooks'
import type {
  TemplateCategory,
  SelectedTemplateItem,
  SavedTemplateFormData,
  GroupField,
  FormComponentType,
  FormBuilderSelection,
  FieldSettingsUpdateHandler,
} from '../types'
import { FormTemplateStatus } from '@dataTypes/forms.type'
import CreateFormEmptyState from './CreateFormEmptyState.component'
import FormBuilderContent from './FormBuilderContent.component'
import './FormContentArea.css'

interface FormContentAreaProps {
  formBuilderKey?: number
  showFormBuilder: boolean
  hasAnyForms: boolean
  selectedCategory: string | null
  selectedTemplateItem: SelectedTemplateItem | null
  templateCategories: TemplateCategory[]
  initialFormTitle?: string
  onCategorySelect: (categoryTitle: string) => void
  onCreateForms: () => void
  componentClickRef?: React.MutableRefObject<
    ((component: FormComponentType) => void) | null
  >
  previewToggleRef?: React.MutableRefObject<{
    isPreviewMode: boolean
    togglePreview: () => void
  } | null>
  onStatusChange?: (
    templateName: string,
    categoryTitle: string,
    newStatus: FormTemplateStatus,
  ) => void
  formDataRef?: React.MutableRefObject<
    | (() => {
        formTitle: string
        groups: GroupField[]
        formStatus: FormTemplateStatus
      })
    | null
  >
  savedTemplateFormData?: Record<string, SavedTemplateFormData>
  onFormDataChange?: () => void
  selectedFieldId?: string | null
  onFieldSelect?: (field: FormBuilderSelection | null) => void
  fieldSettingsRef?: React.MutableRefObject<FieldSettingsUpdateHandler | null>
  readOnly?: boolean
}

const FormContentArea: React.FC<FormContentAreaProps> = ({
  formBuilderKey = 0,
  showFormBuilder,
  hasAnyForms,
  selectedCategory,
  selectedTemplateItem,
  templateCategories,
  initialFormTitle,
  onCategorySelect,
  onCreateForms,
  componentClickRef,
  previewToggleRef,
  onStatusChange,
  formDataRef,
  savedTemplateFormData,
  onFormDataChange,
  selectedFieldId,
  onFieldSelect,
  fieldSettingsRef,
  readOnly = false,
}) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false)
  const isSidePanelOpen = useAppSelector((state) => state.ui.isSidePanelOpen)

  useEffect(() => {
    if (previewToggleRef?.current) {
      setIsPreviewMode(previewToggleRef.current.isPreviewMode)
    }

    const interval = setInterval(() => {
      if (previewToggleRef?.current) {
        setIsPreviewMode((prev) => {
          const current = previewToggleRef.current?.isPreviewMode ?? false
          return current !== prev ? current : prev
        })
      }
    }, 100)

    return () => clearInterval(interval)
  }, [previewToggleRef])

  useEffect(() => {
    if (!showFormBuilder && componentClickRef) {
      componentClickRef.current = null
    }
  }, [showFormBuilder, componentClickRef])

  const renderContent = (): React.ReactNode => {
    if (showFormBuilder) {
      return (
        <FormBuilderContent
          key={formBuilderKey}
          selectedTemplateItem={selectedTemplateItem}
          selectedCategory={selectedCategory}
          templateCategories={templateCategories}
          initialFormTitle={initialFormTitle}
          onCategorySelect={onCategorySelect}
          componentClickRef={componentClickRef}
          previewToggleRef={previewToggleRef}
          onStatusChange={onStatusChange}
          formDataRef={formDataRef}
          savedTemplateFormData={savedTemplateFormData}
          onFormDataChange={onFormDataChange}
          selectedFieldId={selectedFieldId}
          onFieldSelect={onFieldSelect}
          fieldSettingsRef={fieldSettingsRef}
          readOnly={readOnly}
        />
      )
    }
    if (!hasAnyForms) {
      return <CreateFormEmptyState onCreateForms={onCreateForms} />
    }
    return <CreateFormEmptyState onCreateForms={onCreateForms} />
  }

  return (
    <div className="form-content-area-wrapper">
      {isPreviewMode && !readOnly && (
        <h2 className="form-preview-section-title">Form Preview</h2>
      )}
      <div
        className={`create-form-content ${
          showFormBuilder ? 'has-form-builder' : ''
        } ${showFormBuilder && isSidePanelOpen ? 'patient-sidepanel-open' : ''}`}
      >
        {renderContent()}
      </div>
    </div>
  )
}

export default FormContentArea
